/**
 * VulForm API Client
 * Handles all API communication with VulForm backend
 */

import {
  ApiClientConfig,
  ApiResponse,
  FormTemplate,
  SubmissionRequest,
  SubmissionResponse,
  AnalyticsEvent,
  ExportRequest,
  ExportResult,
  ValidationRequest,
  ValidationResponse,
  FormError as IFormError,
  FieldValidation,
} from '../types';
import { resolveApiUrl, normalizeApiUrl, isValidVulFormUrl } from '../config';

export class VulFormApiClient {
  private config: Required<ApiClientConfig>;

  constructor(config: ApiClientConfig) {
    if (!config.apiKey) {
      throw new VulFormError('MISSING_API_KEY', 'API key is required');
    }

    if (!config.apiKey.startsWith('vf_')) {
      throw new VulFormError(
        'INVALID_API_KEY',
        'Invalid API key format. API key must start with "vf_"'
      );
    }

    // Smart URL resolution with fallback hierarchy
    const resolvedBaseUrl = this.resolveBaseUrl(config.baseUrl);

    this.config = {
      baseUrl: resolvedBaseUrl,
      timeout: config.timeout || 10000,
      retries: config.retries || 3,
      debug: config.debug || false,
      apiKey: config.apiKey,
    };

    if (this.config.debug) {
      console.log('[VulForm API] Client initialized:', {
        apiKey: config.apiKey.substring(0, 8) + '...',
        baseUrl: this.config.baseUrl,
        resolvedFrom: config.baseUrl ? 'provided' : 'auto-detected',
      });
    }
  }

  private resolveBaseUrl(providedUrl?: string): string {
    // 1. Use provided URL if valid
    if (providedUrl && isValidVulFormUrl(providedUrl)) {
      return normalizeApiUrl(providedUrl);
    }

    // 2. Auto-resolve based on environment and deployment scenario
    const resolvedUrl = resolveApiUrl(providedUrl, 'auto');

    if (this.config?.debug) {
      console.log('[VulForm API] URL Resolution:', {
        provided: providedUrl,
        resolved: resolvedUrl,
        strategy: 'auto',
      });
    }

    return resolvedUrl;
  }

  /**
   * Get form template by ID
   */
  async getTemplate(templateId: string): Promise<FormTemplate> {
    const response = await this.makeRequest<{ template: FormTemplate }>(
      `/templates/${templateId}`,
      {
        method: 'GET',
      }
    );

    if (!response.data?.template) {
      throw new VulFormError('TEMPLATE_NOT_FOUND', `Template ${templateId} not found`);
    }

    return response.data.template;
  }

  /**
   * Submit form data
   */
  async submitForm(data: SubmissionRequest): Promise<SubmissionResponse> {
    const requestData = {
      ...data,
      api_key: this.config.apiKey,
      metadata: {
        ...data.metadata,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
      },
    };

    const response = await this.makeRequest<SubmissionResponse>('/submit', {
      method: 'POST',
      body: JSON.stringify(requestData),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.success || !response.data) {
      throw new VulFormError(
        response.error?.code || 'SUBMISSION_FAILED',
        response.error?.message || 'Form submission failed'
      );
    }

    return response.data;
  }

  /**
   * Validate individual field
   */
  async validateField(
    field: string,
    value: any,
    validation: FieldValidation
  ): Promise<string | null> {
    // Client-side validation first
    const clientError = this.validateFieldClientSide(field, value, validation);
    if (clientError) {
      return clientError;
    }

    // Server-side validation for complex rules
    if (validation.pattern || field === 'email') {
      try {
        const response = await this.makeRequest<ValidationResponse>('/validate', {
          method: 'POST',
          body: JSON.stringify({
            field,
            value,
            rules: validation,
          } as ValidationRequest),
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.data && !response.data.valid) {
          return response.data.error || 'Validation failed';
        }
      } catch (error) {
        // Fallback to client-side validation if server validation fails
        if (this.config.debug) {
          console.warn(
            '[VulForm API] Server validation failed, using client-side validation:',
            error
          );
        }
      }
    }

    return null;
  }

  /**
   * Track analytics event
   */
  async trackEvent(event: AnalyticsEvent): Promise<void> {
    try {
      await this.makeRequest('/analytics/events', {
        method: 'POST',
        body: JSON.stringify({
          events: [event],
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      if (this.config.debug) {
        console.warn('[VulForm API] Failed to track analytics event:', error);
      }
      // Don't throw analytics errors
    }
  }

  /**
   * Export form template as code
   */
  async exportTemplate(request: ExportRequest): Promise<ExportResult> {
    const response = await this.makeRequest<ExportResult>(
      `/templates/${request.templateId}/export`,
      {
        method: 'POST',
        body: JSON.stringify({ options: request.options }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.data) {
      throw new VulFormError('EXPORT_FAILED', 'Failed to export template');
    }

    return response.data;
  }

  /**
   * Client-side field validation
   */
  private validateFieldClientSide(
    field: string,
    value: any,
    validation: FieldValidation
  ): string | null {
    // Required field validation
    if (
      validation.required &&
      (!value || (typeof value === 'string' && value.trim().length === 0))
    ) {
      return validation.customMessage || `${field} is required`;
    }

    // Skip validation for empty optional fields
    if (!value || value.length === 0) {
      return null;
    }

    // String validations
    if (typeof value === 'string') {
      if (validation.minLength && value.length < validation.minLength) {
        return validation.customMessage || `Must be at least ${validation.minLength} characters`;
      }

      if (validation.maxLength && value.length > validation.maxLength) {
        return (
          validation.customMessage || `Must be no more than ${validation.maxLength} characters`
        );
      }

      // Basic email validation
      if (field === 'email' && !this.isValidEmail(value)) {
        return validation.customMessage || 'Please enter a valid email address';
      }

      // Pattern validation
      if (validation.pattern && !new RegExp(validation.pattern).test(value)) {
        return validation.customMessage || 'Invalid format';
      }
    }

    // Number validations
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
      const numValue = Number(value);

      if (validation.min !== undefined && numValue < validation.min) {
        return validation.customMessage || `Must be at least ${validation.min}`;
      }

      if (validation.max !== undefined && numValue > validation.max) {
        return validation.customMessage || `Must be no more than ${validation.max}`;
      }
    }

    return null;
  }

  /**
   * Basic email validation
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Make HTTP request with retry logic
   */
  private async makeRequest<T>(endpoint: string, options: RequestInit): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.config.retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

        const response = await fetch(url, {
          ...options,
          headers: {
            Authorization: `Bearer ${this.config.apiKey}`,
            'X-VulForm-SDK': 'core/1.0.0',
            ...options.headers,
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Parse response
        let responseData: any;
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          responseData = { message: await response.text() };
        }

        // Handle HTTP errors
        if (!response.ok) {
          if (response.status === 429) {
            // Rate limit handling
            const retryAfter = parseInt(response.headers.get('Retry-After') || '60');
            throw new VulFormError(
              'RATE_LIMIT_EXCEEDED',
              `Rate limit exceeded. Try again in ${retryAfter} seconds`,
              response.status,
              { retryAfter }
            );
          }

          throw new VulFormError(
            responseData.code || 'API_ERROR',
            responseData.message || `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            responseData
          );
        }

        // Success response
        return {
          success: true,
          data: responseData,
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Don't retry on certain errors
        if (error instanceof VulFormError) {
          if (['INVALID_API_KEY', 'TEMPLATE_NOT_FOUND', 'VALIDATION_ERROR'].includes(error.code)) {
            throw error;
          }
        }

        // Exponential backoff for retries
        if (attempt < this.config.retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          await this.sleep(delay);
          continue;
        }

        // All retries exhausted
        if (error instanceof VulFormError) {
          throw error;
        }

        throw new VulFormError(
          'NETWORK_ERROR',
          lastError.message || 'Network request failed',
          0,
          lastError
        );
      }
    }

    // This should never be reached
    throw new VulFormError('UNKNOWN_ERROR', 'An unknown error occurred');
  }

  /**
   * Sleep utility for retries
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

/**
 * VulForm Error class
 */
export class VulFormError extends Error {
  public code: string;
  public statusCode?: number;
  public details?: any;

  constructor(code: string, message: string, statusCode?: number, details?: any) {
    super(message);
    this.name = 'VulFormError';
    this.code = code;
    this.statusCode = statusCode;
    this.details = details;
  }
}

export default VulFormApiClient;
